//---------------------------------------------------------
// Soft #40: react-query hook for one allowlisted chart.
//---------------------------------------------------------
import {useQuery} from '@tanstack/react-query';
import {fetchMetricsChart} from 'connector/oam/metricsChartsApi';
import {useMetricsRange} from 'metricsRange';
import {useEffect, useRef, useState} from 'react';
import {ITimeSeriesPoint} from 'types/global';
import {IMetricsChartResult, MetricsChartName} from 'types/metricsCharts';

// Prometheus scrapes every 30s; polling faster only repeats the same value.
export const METRICS_CHART_POLL_MS = 30_000;

// Charts follow the dashboard's selected instance; nothing is fetched until one
// is selected, and a new selection is a new query key.
export function useMetricsChart(name: MetricsChartName, instanceId: number | null | undefined) {
	const range = useMetricsRange();
	return useQuery<IMetricsChartResult, Error>({
		queryKey: ['metrics-chart', instanceId ?? null, name, range],
		queryFn: () => fetchMetricsChart(instanceId as number, name, range),
		enabled: !!instanceId,
		refetchInterval: METRICS_CHART_POLL_MS,
		refetchIntervalInBackground: false,
		staleTime: 10_000,
		retry: false,
	});
}

// Keeps the last N scalar readings of a chart for a sparkline. Each reading is
// the server's instant value at queriedAt.
export function useScalarHistory(result: IMetricsChartResult | undefined, pick: (r: IMetricsChartResult) => number | null, maxPoints = 60) {
	const [history, setHistory] = useState<ITimeSeriesPoint<number>[]>([]);
	// The picker is usually an inline arrow; keep the latest in a ref so the
	// effect only re-runs when a new result arrives.
	const pickRef = useRef(pick);
	pickRef.current = pick;
	useEffect(() => {
		if (!result) return;
		const v = pickRef.current(result);
		if (v === null) return;
		const ts = Date.parse(result.queriedAt) || Date.now();
		setHistory(prev => (prev.length && prev[prev.length - 1].timestamp === ts ? prev : [...prev, {timestamp: ts, data: v}].slice(-maxPoints)));
	}, [result, maxPoints]);
	return history;
}
